import React, { useState } from 'react';
import { ASINSalesHistoryRow } from '@/types/asinSalesHistory';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';

interface ASINViewerTableProps {
  data: ASINSalesHistoryRow[];
}

type SortField = 'date' | 'traffic' | 'conversionRate' | 'units' | 'revenue' | 'ppcSpend' | 'grossProfit' | 'sevenDayAvgRevenue' | 'sevenDayAvgPPCSpend' | 'sevenDayAvgGrossProfit' | 'tacos' | 'outOfStock';
type SortDirection = 'asc' | 'desc';

export const ASINViewerTable: React.FC<ASINViewerTableProps> = ({ data }) => {
  const [sortField, setSortField] = useState<SortField>('date');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  };

  const formatPercent = (value: number) => {
    return `${value.toFixed(2)}%`;
  };

  const formatDate = (date: Date) => {
    return new Date(date).toLocaleDateString('en-US', { 
      month: '2-digit', 
      day: '2-digit', 
      year: 'numeric' 
    });
  };

  // Sort data - most recent first by default
  const sortedData = React.useMemo(() => {
    const sorted = [...data].sort((a, b) => {
      let aValue: any;
      let bValue: any;

      switch (sortField) {
        case 'date':
          aValue = new Date(a.date).getTime();
          bValue = new Date(b.date).getTime();
          break;
        case 'traffic':
          aValue = a.traffic;
          bValue = b.traffic;
          break;
        case 'conversionRate':
          aValue = a.conversionRate;
          bValue = b.conversionRate;
          break;
        case 'units':
          aValue = a.units;
          bValue = b.units;
          break;
        case 'revenue':
          aValue = a.revenue;
          bValue = b.revenue;
          break;
        case 'ppcSpend':
          aValue = a.ppcSpend;
          bValue = b.ppcSpend;
          break;
        case 'grossProfit':
          aValue = a.grossProfit;
          bValue = b.grossProfit;
          break;
        default:
          aValue = new Date(a.date).getTime();
          bValue = new Date(b.date).getTime();
      }

      return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
    });

    return sorted;
  }, [data, sortField, sortDirection]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) return <ArrowUpDown className="h-3 w-3 ml-1 opacity-50" />;
    return sortDirection === 'asc'
      ? <ArrowUp className="h-3 w-3 ml-1" />
      : <ArrowDown className="h-3 w-3 ml-1" />;
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg font-semibold">Performance Details</CardTitle>
        <p className="text-sm text-muted-foreground">{sortedData.length} records</p>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b">
                <th className="text-left py-3 px-4 font-medium text-gray-600 dark:text-gray-400">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleSort('date')}
                    className="h-auto p-0 hover:bg-transparent font-medium"
                  >
                    Date <SortIcon field="date" />
                  </Button>
                </th>
                <th className="text-right py-3 px-4 font-medium text-gray-600 dark:text-gray-400">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleSort('traffic')}
                    className="h-auto p-0 hover:bg-transparent font-medium"
                  >
                    Traffic <SortIcon field="traffic" />
                  </Button>
                </th>
                <th className="text-right py-3 px-4 font-medium text-gray-600 dark:text-gray-400">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleSort('conversionRate')}
                    className="h-auto p-0 hover:bg-transparent font-medium"
                  >
                    Conversion Rate <SortIcon field="conversionRate" />
                  </Button>
                </th>
                <th className="text-right py-3 px-4 font-medium text-gray-600 dark:text-gray-400">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleSort('units')}
                    className="h-auto p-0 hover:bg-transparent font-medium"
                  >
                    Units <SortIcon field="units" />
                  </Button>
                </th>
                <th className="text-right py-3 px-4 font-medium text-gray-600 dark:text-gray-400">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleSort('revenue')}
                    className="h-auto p-0 hover:bg-transparent font-medium"
                  >
                    Revenue <SortIcon field="revenue" />
                  </Button>
                </th>
                <th className="text-right py-3 px-4 font-medium text-gray-600 dark:text-gray-400">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleSort('ppcSpend')}
                    className="h-auto p-0 hover:bg-transparent font-medium"
                  >
                    PPC Spend <SortIcon field="ppcSpend" />
                  </Button>
                </th>
                <th className="text-right py-3 px-4 font-medium text-gray-600 dark:text-gray-400">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleSort('grossProfit')}
                    className="h-auto p-0 hover:bg-transparent font-medium"
                  >
                    Gross Profit <SortIcon field="grossProfit" />
                  </Button>
                </th>
              </tr>
            </thead>
            <tbody>
              {sortedData.map((row, index) => (
                <tr 
                  key={`${row.date}-${index}`} 
                  className={`border-b ${
                    index % 2 === 0 
                      ? 'bg-green-50 dark:bg-green-900/10' 
                      : 'bg-white dark:bg-gray-800'
                  } hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors`}
                >
                  <td className="py-3 px-4 text-gray-900 dark:text-gray-100">
                    {formatDate(row.date)}
                  </td>
                  <td className="py-3 px-4 text-right text-gray-900 dark:text-gray-100">
                    {row.traffic.toLocaleString()}
                  </td>
                  <td className="py-3 px-4 text-right text-gray-900 dark:text-gray-100">
                    {formatPercent(row.conversionRate)}
                  </td>
                  <td className="py-3 px-4 text-right text-gray-900 dark:text-gray-100">
                    {row.units.toLocaleString()}
                  </td>
                  <td className="py-3 px-4 text-right text-gray-900 dark:text-gray-100">
                    {formatCurrency(row.revenue)}
                  </td>
                  <td className="py-3 px-4 text-right text-gray-900 dark:text-gray-100">
                    {formatCurrency(row.ppcSpend)}
                  </td>
                  <td className="py-3 px-4 text-right font-medium">
                    <span className={row.grossProfit >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}>
                      {formatCurrency(row.grossProfit)}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {sortedData.length === 0 && (
            <div className="text-center py-12 text-muted-foreground">
              No data available
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};